import Head from "next/head";
import { BuilderShowCaseSection } from "components/CLM/BuilderShowCase";
import CLMHero2 from "components/CLM/CLMHero2";
import CTASection from "components/CLM/CTASection";
import EqualDivisionSection from "components/CLM/EqualDivisionSection";
import FAQ2 from "components/CLM/FAQ2";
import { CLMButtonPrimary } from "components/CLM/v3/Buttons";
import { useEffect, useRef, useState } from "react";
import { BASE_ASSETS_URL } from "@constants/constants";

const CLMHeroData = {
  // title: "Reporting",
  heading: "Discover what’s inside your contracts",
  description:
    "BoloSign helps you find key data, improve workflows, and make smarter decisions—all from your contracts.",
  image: `${BASE_ASSETS_URL}/v2/clm/clm/contract-analysis-hero.gif`,
  btnText: "Take a Quick Demo",
  btnUrl: "/clm/request-demo",
};
const contractAnalysisFaqData = [
  {
    question: "What insights can I discover with BoloSign’s Contract Analysis?",
    answer:
      "• Get a clear view of how many contracts you manage and the different categories (e.g., sales, procurement, NDAs) over time.\n\n• Evaluate how efficiently your approval and review workflows are operating, so you can spot slow points.\n\n• Anticipate upcoming renewals or expirations and stay ahead of important deadlines.",
  },
  {
    question: "How do these analytics help my legal team stay proactive?",
    answer:
      "• Track key dates—like renewal and expiration—so your team can start negotiations well before deadlines.\n\n• Spot recurring bottlenecks (for instance, approval delays) and streamline those stages to keep contracts moving.\n\n• Align contract strategies with broader business objectives by analyzing trends in your contract data.",
  },
  {
    question: "Who should leverage BoloSign’s Contract Analysis tools?",
    answer:
      "• **Legal Departments** wanting to tighten contract cycle times and reduce manual follow-ups.\n\n• **Contract Managers** who need in-depth reporting on where each agreement stands—draft, review, or signed.\n\n• **Business Leaders & Ops Teams** seeking a snapshot of contractual risks, obligations, and operational efficiency.",
  },
  {
    question: "What kinds of data and visualizations are available?",
    answer:
      "• **Lifecycle Tracking:** See each contract’s journey—from initial draft to final signature—in one view.\n\n• **Timeline Comparisons:** Measure planned versus actual turnaround on approvals, redlining, and renewals.\n\n• **Segmented Overviews:** Break down contract statuses (e.g., Drafted, Pending Approval, Active, Expired) by department, contract type, or region.",
  },
  {
    question: "Can I customize these dashboards to fit my organization?",
    answer:
      "• **Select Essential Metrics:** Choose which data points matter most—approval times, renewal forecasts, or volume by contract type.\n\n• **Apply Flexible Filters:** Narrow the view to a specific department, business unit, or time span to analyze trends in detail.\n\n• **Save Custom Views:** Create and store dashboard layouts so your team can instantly access the analyses they use most.",
  },
  {
    question:
      "How does BoloSign protect the security and compliance of my contract information?",
    answer:
      "• We adhere to industry-leading encryption standards—both in transit and at rest—so contract content remains confidential.\n\n• Role-based access controls ensure only authorized users can view or act on contract analytics.\n\n• Regular security audits and compliance checks (ESIGN, GDPR, etc.) keep your data protected. For a full list of certifications and policies, please visit https://security.boloforms.com/",
  },
];

const showcaseData = [
  {
    heading: "Gain Full Control Over Your Contract Lifecycle",
    subHeading:
      "Track trends, monitor key contract types, and stay on top of every stage—from draft to execution.",
    imgSrc: `${BASE_ASSETS_URL}/v2/clm/clm/analysis-initiated-this-year.png`,
  },
  {
    heading: "Optimize Approval Workflows with Actionable Insights",
    subHeading:
      "Track approval status, monitor turnaround times, and uncover trends to drive faster, smarter contract approvals.",
    imgSrc: `${BASE_ASSETS_URL}/v2/clm/clm/analysis-approvals.png`,
  },
  {
    heading: "Improve Contract Review Efficiency with Real-Time Insights",
    subHeading:
      "Track review progress, analyze timelines by contract type, and uncover trends to streamline your entire review process.",
    imgSrc: `${BASE_ASSETS_URL}/v2/clm/clm/analysis-reviews.png`,
  },
  {
    heading: "Accelerate Contract Cycles with Turnaround Time Insights",
    subHeading:
      "Analyze time spent at each stage—from drafting to redlining—and compare review cycles by contract type to optimize every workflow.",
    imgSrc: `${BASE_ASSETS_URL}/v2/clm/clm/analysis-average-drafting.png`,
  },
];

const ContractAnalysis = ({ isDarkMode, setIsDarkMode }) => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const handleScroll = () => {
      const rect = section.getBoundingClientRect();
      const topTrigger = 120; // or some margin like 100
      const bottomTrigger = window.innerHeight;

      const isInView =
        rect.top <= topTrigger && rect.bottom >= bottomTrigger / 2;

      setIsDarkMode(isInView);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <Head>
        <title>Contract Analysis & Reporting | BoloSign CLM</title>
        <meta 
          name="description"
          content="Gain deep insights into your contract workflows with BoloSign's Contract Analysis. Monitor key metrics, optimize processes, and make data-driven decisions."
        />
        <meta
          name="keywords"
          content="Contract Analysis, Contract Reporting, CLM Analytics, Contract Workflows, BoloSign CLM"
        />
        <link rel="canonical" href="https://www.bolosign.com/clm/analytics/" />
      </Head>
      <CLMHero2 CLMHeroData={CLMHeroData} />

      <div className="text-start md:text-center">
        <h2 className="text-3xl font-semibold md:text-5xl px-6 md:px-0  mb-4 pt-10 md:pt-20 ">
          Total visibility into your contract workflow
        </h2>
        <p className="text-xl md:text-2xl w-[85%] xl:w-[60%] mx-auto md:-mb-16">
          BoloSign offers real-time dashboards with 30+ key metrics, helping you
          monitor, analyze, and optimize your contracting process across every
          stage of the lifecycle.
        </p>

        <EqualDivisionSection
          // title="Templates"
          // heading="Easily search and stay updated on important contracts"
          image={`${BASE_ASSETS_URL}/v2/clm/clm/contract-analysis-visibility-formated.gif`}
          bgColor="bg-transparent"
          imageRight={true}
          checks={[
            "Track the full contract journey — Monitor lifecycle stages, approval times, and review turnaround to identify delays and optimize performance.",
            "Drill into the data — Use filters like date range, contract type, user, and workflow to uncover trends and gain deeper insights.",
            "Drive alignment across teams — Share clear, actionable data to keep legal, sales, and business stakeholders on the same page.",
          ]}
          opinion="“Having data makes it easier to provide concrete proof instead of estimates.”"
          imageRounded={false}
        />
      </div>

      <div className="text-start md:text-center bg-cream">
        <h2 className="text-3xl md:text-5xl mb-6 pt-20 md:pt-32 font-semibold px-6 md:px-0">
          Optimize your contracting with actionable insights
        </h2>
        <p className="text-xl md:text-2xl w-[85%] xl:w-[60%] mx-auto md:-mb-16">
          BoloSign shows you what’s working—and what’s not—so you can streamline
          processes, reduce delays, and improve outcomes across your contract
          workflow.
        </p>

        <EqualDivisionSection
          // title="Templates"
          // heading="Easily search and stay updated on important contracts"
          image={`${BASE_ASSETS_URL}/v2/clm/clm/contract-analysis-average-time.png`}
          bgColor="bg-transparent"
          imageRight={false}
          checks={[
            "Eliminate approval delays – Identify and fix bottlenecks in your contract review and approval workflows to speed up deal cycles.",
            "Simplify high-volume negotiations – Analyze trends to optimize workflows for frequently used contract types like NDAs and MSAs.",
            "Customize SLAs by contract type – Leverage turnaround time data to set realistic, data-driven expectations for different agreements.",
          ]}
          opinion="“With data, we found contracts get signed faster when we share responsibility for filling in details—it’s improved our turnaround time noticeably.”"
        />
      </div>

      <div className="text-start md:text-center">
        <h2 className="text-3xl md:text-5xl mb-6 pt-20 md:pt-32 font-semibold px-6 md:px-0">
          Highlight the impact of your legal team
        </h2>
        <p className="text-xl md:text-2xl w-[85%] xl:w-[60%] mx-auto md:-mb-16">
          BoloSign equips Legal with the data they need to demonstrate
          value—track performance, share success metrics, and respond to
          business questions with clear, contract-driven insights.
        </p>

        <EqualDivisionSection
          // title="Templates"
          // heading="Easily search and stay updated on important contracts"
          image={`${BASE_ASSETS_URL}/v2/clm/clm/analysis-avg-response.png`}
          bgColor="bg-transparent"
          imageRight={true}
          checks={[
            "Track improvements over time – Use Turnaround Time metrics to highlight faster contract cycles and workflow efficiency.",
            "Report with confidence – Build quarterly reports to spotlight team wins and uncover areas for improvement.",
            "Drive alignment across teams – Share data-driven insights that resonate with business stakeholders and leadership.",
          ]}
          opinion="“This helps us show leadership how much we’re handling—and makes a strong case for expanding our team.”"
        />
      </div>

      <div
        className={`transition-colors duration-300  ${
          isDarkMode ? "bg-dark text-white" : "bg-white text-black"
        }`}
      >
        <section ref={sectionRef} className="pt-20">
          <BuilderShowCaseSection
            heading="Turn Contracts Into Insights with Powerful Dashboards"
            categories={["Lifecycle", "Approval", "Review", "Turnaround Time"]}
            showcaseData={showcaseData}
            isDarkMode={isDarkMode}
          />
        </section>
      </div>
      {/* FAQ Section */}
      <section className="bg-[#f4f2f0] text-black py-16 md:px-10 px-4 xl:px-20 text-center md:pt-24 pt-12 ">
        <FAQ2 faqData={contractAnalysisFaqData} heading={"FAQs"} />
      </section>
    </>
  );
};

export default ContractAnalysis;
